import React, { useState, useEffect } from 'react';
import SignupForm from 'features/onboarding/pages/SignupForm';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, isFirebasePopupDismissal } from 'shared/config/firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'shared/services/api/axiosInstance';
import preloadData from 'shared/utils/preloadService';
import { markWorkspaceSetupPending } from 'shared/utils/workspaceSetupPending';
import { fetchAndApplySubscriptionForDefaultTeam, syncTrodoIdentifyAndTeam } from 'shared/utils/authSessionBootstrap';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import { motion } from 'framer-motion';
import { useAppDialog } from 'shared/context/AppDialogContext';
import GoogleAuthButton from 'features/onboarding/components/GoogleAuthButton';
import WorkspaceSetupOverlay from 'features/onboarding/components/WorkspaceSetupOverlay';
import TrodoWordmark from 'features/onboarding/components/TrodoWordmark';
import AuthBrandPanel, { AuthTestimonial } from 'features/onboarding/components/AuthBrandPanel';

const ACCENT = 'var(--trodo-primary)';

function AuthShell({ children, loading, agreement = true }) {
  return (
    <div
      className="min-h-screen w-full relative overflow-hidden flex flex-col"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      {/* trodo.ai's range along the bottom, wind over it */}
      <AuthBrandPanel />

      {/* logo: top-left, above everything */}
      <div className="relative z-10 shrink-0 px-7 pt-6 pb-0">
        <TrodoWordmark href="/login" />
      </div>

      {/* what customers say (wide screens), and the form */}
      <div className="relative z-10 flex flex-1 items-start justify-center px-4 pt-[7vh] pb-8 lg:items-center lg:pt-0 lg:pb-[14vh]">
        <div className="w-full max-w-[1080px] flex items-center justify-center lg:justify-between gap-16">
        <div className="hidden lg:block flex-1 min-w-0">
          <AuthTestimonial />
        </div>
        <div
          className="auth-glass w-full max-w-[390px] shrink-0 rounded-2xl border px-8 pt-9 pb-8"
          style={{ borderColor: 'var(--border-default)' }}
        >
          {children}
          {agreement && <p className="mt-6 pt-5 border-t text-center text-[12px] leading-relaxed" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-default)' }}>
            By continuing, you agree to our{' '}
            <a href="https://trodo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[color:var(--text-primary)] transition-colors">Privacy</a>
            {' '}and{' '}
            <a href="https://trodo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[color:var(--text-primary)] transition-colors">Terms</a>.
          </p>}
        </div>
        </div>
      </div>

      {/* the line under the range */}
      <div className="relative z-10 shrink-0 px-4 pb-6 text-center">
        <p className="text-[16px] sm:text-[19px] leading-snug tracking-[-0.01em]" style={{ color: 'var(--text-primary)' }}>
          Every trace evaluated, for a fraction of the cost.
        </p>

      </div>

      {loading && <WorkspaceSetupOverlay />}

      {/* glass card theming */}
      <style>{`
        .auth-glass {
          background: rgba(255, 255, 255, 0.86);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: 0 8px 32px -8px rgba(0,0,0,0.10), 0 1px 0 rgba(255,255,255,0.6) inset;
        }
        :root[data-theme="dark"] .auth-glass {
          background: rgba(10, 10, 11, 0.84);
          box-shadow: 0 8px 32px -8px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset;
        }
      `}</style>
    </div>
  );
}

function Interface() {
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/signup') {
      setShowLogin(false);
    } else {
      setShowLogin(true);
    }
  }, [location.pathname]);

  const toggleLoading = (isLoading) => {
    setLoading(isLoading !== undefined ? isLoading : !loading);
  };

  const toggleForm = () => {
    // Preserve ?next so the user lands back on the original deep link
    // even if they bounce between login and signup before authenticating.
    const search = location.search || '';
    trackClientEvent(
      'auth_method_toggled',
      { from: showLogin ? 'login' : 'signup', to: showLogin ? 'signup' : 'login' },
      { category: 'auth' }
    );
    if (showLogin) {
      navigate(`/signup${search}`);
    } else {
      navigate(`/login${search}`);
    }
  };

  return (
    <AuthShell loading={loading} agreement={showLogin}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <h1
          className="text-[2rem] sm:text-[2.25rem] leading-tight mb-8 text-[color:var(--text-primary)] text-left font-serif font-normal tracking-tight"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {showLogin ? 'Get started' : 'Create an account'}
        </h1>

        {showLogin ? (
          <LoginForm onSignupClick={toggleForm} toggleLoading={toggleLoading} />
        ) : (
          <SignupForm onBackToLogin={toggleForm} toggleLoading={toggleLoading} />
        )}
      </motion.div>
    </AuthShell>
  );
}

/**
 * If the user landed on /login?next=<path> (or signed in from a ProtectedRoute
 * that captured a deep link), resolve that path safely.
 *
 * Only same-origin absolute paths are honored — never an external URL — to
 * avoid open redirects. /login itself is excluded so we don't loop.
 */
function resolveSafeNextUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('next');
    if (!raw) return null;
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
    if (decoded.startsWith('/login') || decoded.startsWith('/signup')) return null;
    return decoded;
  } catch (_) {
    return null;
  }
}

function LoginForm({ onSignupClick, toggleLoading }) {
  const { alert: appAlert } = useAppDialog();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

  useEffect(() => {
    if (step === 1) {
      setIsFormValid(email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    } else {
      setIsFormValid(password.trim() !== '');
    }
  }, [email, password, step]);

  const submitLogin = async () => {
    let redirecting = false;
    // See SignupForm.js's handleSubmit for why this exists: WorkspaceSetupOverlay
    // is `fixed inset-0 z-[10000]`, the app's dialogs render at z-1400, so an
    // `await appAlert(...)` fired while still loading draws its OK button
    // behind the overlay — invisible, unclickable, the promise never resolves,
    // `finally` never runs, and the spinner stays up forever. Take the overlay
    // down the moment we know we're not redirecting, before anything that
    // waits on a click. Left alone on the redirect paths below on purpose —
    // the overlay is supposed to persist through the navigation there.
    let settled = false;
    const stopLoading = () => {
      if (!settled) { settled = true; toggleLoading(false); }
    };
    try {
      toggleLoading(true);
      trackClientEvent('log_in_attempted', { login_method: 'password' }, { category: 'auth' });
      const response = await axiosInstance.post('/auth/login', {
        email: savedEmail,
        password,
      });

      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));

        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          await Promise.all([fetchAndApplySubscriptionForDefaultTeam(defaultTeam, 'login'), preloadData(true)]);
        } else {
          localStorage.removeItem('selectedTeam');
          localStorage.removeItem('selectedTeamData');
        }

        // Analytics does not hold up the app. identify and the group calls are
        // three round trips, and awaiting them here put ~1s between "signed in"
        // and the first screen. teamContext identifies again as soon as the
        // landing page mounts, so nothing is lost by letting this run behind
        // the redirect.
        void syncTrodoIdentifyAndTeam(response.data.user, defaultTeam);

        markWorkspaceSetupPending();
        redirecting = true;
        const next = resolveSafeNextUrl();
        window.location.href = next
          || (defaultTeam
            ? `/${encodeURIComponent(defaultTeam.name)}/chat`
            : '/new-project');
        return;
      }
      stopLoading();
      await appAlert(response.data.message || 'Invalid credentials', { title: 'Login', variant: 'error' });
    } catch (error) {
      // Before any alert, always — this is the path that used to hang.
      stopLoading();

      if (error.response && error.response.status === 402 && error.response.data?.requiresVerification) {
        if (error.response.data.accessToken) {
          localStorage.setItem('accessToken', error.response.data.accessToken);
        }
        if (error.response.data.user) {
          localStorage.setItem('User', JSON.stringify(error.response.data.user));
        }
        await appAlert(
          error.response.data.message || 'A new OTP has been sent to your email. Please verify to complete login.',
          { title: 'Verify your email' }
        );
        redirecting = true;
        window.location.href = '/signup?email=' + encodeURIComponent(savedEmail);
        return;
      }

      if (error.response && [401, 404, 402].includes(error.response.status)) {
        await appAlert(error.response.data.message, { title: 'Login', variant: 'error' });
      } else {
        console.error('Error during login:', error);
        await appAlert('An error occurred. Please try again.', { title: 'Login', variant: 'error' });
      }
    } finally {
      // Belt and braces: stopLoading() is idempotent, so this only fires if
      // some path above returned without reaching one of the explicit calls.
      if (!redirecting) stopLoading();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setSavedEmail(email);
        setStep(2);
      }
      return;
    }
    await submitLogin();
  };

  const handleBackToEmail = () => {
    setStep(1);
    setPassword('');
    setForgotPasswordMessage('');
  };

  const handleResetPassword = async () => {
    if (!savedEmail.trim()) {
      setForgotPasswordMessage('Please enter your email address');
      return;
    }
    try {
      trackClientEvent('password_reset_requested', { from: 'login_form' }, { category: 'auth' });
      await axiosInstance.post('/auth/forgot-password', { email: savedEmail });
      setForgotPasswordMessage('Password reset link has been sent to your email');
      setTimeout(() => setForgotPasswordMessage(''), 3000);
    } catch (error) {
      console.error('Error sending reset email:', error);
      setForgotPasswordMessage(error.response?.data?.message || 'Error sending reset email. Please try again.');
    }
  };

  const googleLogin = async () => {
    let redirecting = false;
    // See submitLogin above / SignupForm.js's handleSubmit for why this
    // exists — take the overlay down before anything that waits on a click.
    let settled = false;
    const stopLoading = () => {
      if (!settled) { settled = true; toggleLoading(false); }
    };
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
      login_hint: '',
      state: encodeURIComponent(JSON.stringify({ origin: window.location.origin })),
    });

    try {
      toggleLoading(true);
      trackClientEvent('google_oauth_clicked', { surface: 'login' }, { category: 'auth' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // The server signs in whoever this ID token proves, and ignores the
      // name/email below (kept only so an older API still works mid-deploy).
      const idToken = await user.getIdToken();
      const response = await axiosInstance.post('/auth/google-login', {
        idToken,
        name: user.displayName,
        email: user.email,
        auth_provider_info: user.photoURL,
      });

      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));

        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          await Promise.all([fetchAndApplySubscriptionForDefaultTeam(defaultTeam, 'Google login'), preloadData(true)]);
        } else {
          localStorage.removeItem('selectedTeam');
          localStorage.removeItem('selectedTeamData');
        }

        // Analytics does not hold up the app. identify and the group calls are
        // three round trips, and awaiting them here put ~1s between "signed in"
        // and the first screen. teamContext identifies again as soon as the
        // landing page mounts, so nothing is lost by letting this run behind
        // the redirect.
        void syncTrodoIdentifyAndTeam(response.data.user, defaultTeam);

        markWorkspaceSetupPending();
        redirecting = true;
        const next = resolveSafeNextUrl();
        window.location.href = next
          || (defaultTeam
            ? `/${encodeURIComponent(defaultTeam.name)}/chat`
            : '/new-project');
        return;
      }
      stopLoading();
      await appAlert(response.data.message || 'Login failed', { title: 'Google login', variant: 'error' });
    } catch (error) {
      stopLoading();
      if (isFirebasePopupDismissal(error)) return;
      console.error('Error during Google login:', error);
      await appAlert('Google login failed. Please try again.', { title: 'Google login', variant: 'error' });
    } finally {
      if (!redirecting) stopLoading();
    }
  };

  const inputClass =
    'w-full px-4 py-[10px] bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg text-[color:var(--text-primary)] placeholder-[color:var(--text-disabled)] focus:outline-none focus:ring-2 focus:ring-[color:rgb(var(--trodo-primary-rgb)/0.18)] focus:border-[color:var(--trodo-primary)] focus:shadow-[0_0_0_4px_rgb(var(--trodo-primary-rgb)/0.06)] text-[14px] transition-shadow';

  return (
    <div className="space-y-6">
      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="sr-only">
            Email
          </label>
          <input
            type="email"
            id="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={step === 2}
            className={`${inputClass} ${step === 2 ? 'bg-[color:var(--border-subtle)] text-[#555555]' : ''}`}
            placeholder="work@email.com"
            autoComplete="email"
            autoFocus={step === 1}
          />
        </div>

        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            <div>
              <label htmlFor="login-password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-14`}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <button
                type="button"
                onClick={handleBackToEmail}
                className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
              >
                Use a different email
              </button>
              <button type="button" onClick={handleResetPassword} className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors">
                Forgot password?
              </button>
            </div>
            {forgotPasswordMessage && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  forgotPasswordMessage.includes('sent')
                    ? 'bg-success-soft text-success border border-success'
                    : 'bg-danger-soft text-danger border border-danger'
                }`}
              >
                {forgotPasswordMessage}
              </div>
            )}
          </motion.div>
        )}

        <button
          type="submit"
          disabled={!isFormValid}
          className={`group relative overflow-hidden w-full py-[10px] rounded-lg font-medium text-[14px] transition-all duration-200 ${
            isFormValid
              ? 'text-ink-onBrand hover:-translate-y-[1px] hover:shadow-[0_10px_28px_-10px_rgb(var(--trodo-primary-rgb)/0.55)] active:translate-y-0'
              : 'bg-[color:var(--surface-raised)] text-[color:var(--text-muted)] cursor-not-allowed border border-[color:var(--border-default)]'
          }`}
          style={isFormValid ? { backgroundColor: ACCENT } : undefined}
        >
          <span className="relative z-10">{step === 1 ? 'Continue' : 'Log in'}</span>
          {isFormValid && (
            <span
              aria-hidden
              className="absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 translate-x-0 group-hover:translate-x-[300%] transition-transform duration-700 ease-out pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgb(255 255 255 / 0.28), transparent)' }}
            />
          )}
        </button>
      </form>

      {/* ── or divider ── */}
      <div className="relative flex items-center gap-3 my-1">
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
        <span className="text-[11px] uppercase tracking-[0.12em] select-none" style={{ color: 'var(--text-muted)' }}>or</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
      </div>

      <GoogleAuthButton variant="signin" onClick={googleLogin} />

      <p className="text-center text-sm text-[color:var(--text-muted)] pt-1">
        Don&apos;t have an account?{' '}
        <button type="button" onClick={onSignupClick} className="font-medium" style={{ color: ACCENT }}>
          Get started
        </button>
      </p>
    </div>
  );
}

export default Interface;
